import { getLocale } from '@zercode/i18n';
import { applyContractHeaders } from '@zercode/utils';

import { ApiProblemError, Envelope, ProblemDetails, ResponseMeta } from './envelope';

export interface ApiClientOptions {
  /** e.g. http://localhost:5200 (ControlPlane) or http://localhost:5101 (Identity) */
  baseUrl: string;
  /** access token supplier — tokens live in memory (see @zercode/auth) */
  getAccessToken?: () => string | null;
  /**
   * 401 refresh hook: called once on a 401 response; return true if a new
   * access token was obtained so the request can be retried [STD-API-6].
   */
  onUnauthorized?: () => Promise<boolean>;
  /** extra headers per request, e.g. X-Tenant-Id dev header from @zercode/tenancy */
  getExtraHeaders?: () => Record<string, string>;
  /**
   * FE-0/FE-5: send the httpOnly refresh cookie on cross-origin auth calls (BE-1). Opt-in and
   * OFF by default, so every existing service client is byte-identical (`same-origin`). Enable it
   * only on the auth client that calls /auth/refresh + /auth/logout — the sole cookie consumers.
   */
  sendCredentials?: boolean;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** query string params; undefined values are dropped */
  /**
   * Query-string params; undefined values are dropped. An ARRAY value emits the key REPEATED
   * (`?ids=a&ids=b`), which is what ASP.NET Core minimal-API array binding expects — `ids[0]=` does
   * NOT bind there. An empty array emits nothing.
   */
  query?: Record<string, string | number | boolean | readonly (string | number)[] | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** override the auto-generated Idempotency-Key (e.g. for retry of the SAME logical write) */
  idempotencyKey?: string;
}

export interface ApiResult<TData> {
  data: TData;
  meta: ResponseMeta;
}

export interface ApiClient {
  /**
   * The service origin this client talks to (e.g. http://localhost:5200).
   *
   * Exposed because some responses carry a FLAG rather than a URL and the caller has to build the
   * URL itself — the tenant branding logo is the case that forced it (BRAND-UPLOAD): the server
   * cannot emit an absolute image URL because it does not know its own public origin, and a
   * relative one would resolve against the SPA, not the API.
   */
  readonly baseUrl: string;

  request<TData>(path: string, options?: RequestOptions): Promise<ApiResult<TData>>;
  get<TData>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<TData>>;
  post<TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<TData>>;
  put<TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<TData>>;
  patch<TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<TData>>;
  delete<TData>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<TData>>;
  /**
   * Binary download (e.g. an assignment attachment) — same auth/tenant/401 handling, but the
   * response is NOT the standard envelope; returns the raw Blob. Errors still surface as
   * ProblemDetails [STD-API-3].
   */
  getBlob(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<Blob>;
  /**
   * POST that answers with a FILE rather than an envelope.
   *
   * ⚠ Why this exists alongside `getBlob`: a download whose parameters cannot fit in a URL has to be a
   * POST. The bulk-register template is the case — the server has to embed the tenant's country/gender
   * catalogues in the workbook (it cannot read them itself), and those go in the body.
   *
   * Idempotency-Key rides along automatically like any other write [STD-API-4] — the server's middleware
   * gates on the METHOD, not on whether anything is actually written.
   */
  postBlob(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<Blob>;
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.startsWith('http') ? path : `${baseUrl.replace(/\/$/, '')}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        // append, not set: repeated key per item. String(array) would join with commas, which the
        // server reads as ONE malformed value.
        for (const item of value) url.searchParams.append(key, String(item));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/** Fallback ProblemDetails for non-7807 failures (network, proxy HTML, …) [STD-API-3] */
function synthesizeProblem(status: number, detail: string, correlationId: string): ProblemDetails {
  return {
    type: 'https://docs.zercode.com/errors/unknown',
    title: 'Unexpected error',
    status,
    detail,
    correlationId
  };
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  // Shared fetch: builds headers (auth, tenant, Correlation-Id, Idempotency-Key), sends the
  // request, and retries once through the 401 refresh hook. Returns the raw Response so the
  // caller can unwrap the envelope (JSON) or read a Blob (binary download).
  async function doFetch(path: string, req: RequestOptions, isRetry: boolean): Promise<{ response: Response; correlationId: string }> {
    const method = req.method ?? 'GET';

    // C-1: advertise the active UI locale so the backend localizes ProblemDetails + sets
    // Content-Language (B-6). A per-request header still wins (spread last).
    const base: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': getLocale(),
      ...options.getExtraHeaders?.(),
      ...req.headers
    };
    // H-1: contract headers (Correlation-Id [STD-API-5] always; Idempotency-Key [STD-API-4] on
    // writes) via the shared @zercode/utils seam — authoritative, applied over `base`.
    const { headers, correlationId } = applyContractHeaders(method, base, { idempotencyKey: req.idempotencyKey });

    const token = options.getAccessToken?.();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let bodyInit: BodyInit | undefined;
    if (req.body instanceof FormData) {
      // multipart: let the browser set Content-Type with the boundary
      bodyInit = req.body;
    } else if (req.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      bodyInit = JSON.stringify(req.body);
    }

    let response: Response;
    try {
      response = await fetch(buildUrl(options.baseUrl, path, req.query), {
        method,
        headers,
        body: bodyInit,
        signal: req.signal,
        // Never let the BROWSER HTTP-cache API responses: React Query owns client-side caching, so a
        // browser-cached GET makes a post-mutation refetch return stale data until a hard reload
        // (e.g. a freshly added child not appearing). 'no-store' forces every call to hit the network.
        cache: 'no-store',
        // FE-0/FE-5: only the auth client opts in; all others stay 'same-origin' (no behavior change).
        credentials: options.sendCredentials ? 'include' : 'same-origin'
      });
    } catch (err) {
      throw new ApiProblemError(synthesizeProblem(0, err instanceof Error ? err.message : 'Network error', correlationId));
    }

    // 401 → run the refresh hook once, then retry the request [STD-API-6]
    if (response.status === 401 && !isRetry && options.onUnauthorized) {
      const refreshed = await options.onUnauthorized();
      if (refreshed) {
        // NOTE: retry reuses the same options; a NEW Idempotency-Key is NOT
        // generated for the retry of the same logical write.
        return doFetch(path, { ...req, idempotencyKey: headers['Idempotency-Key'] }, true);
      }
    }

    return { response, correlationId };
  }

  async function throwIfProblem(response: Response, correlationId: string): Promise<void> {
    if (response.ok) return;
    let problem: ProblemDetails;
    try {
      problem = (await response.json()) as ProblemDetails;
      if (typeof problem.status !== 'number') problem.status = response.status;
      problem.correlationId ??= correlationId;
    } catch {
      problem = synthesizeProblem(response.status, response.statusText, correlationId);
    }
    throw new ApiProblemError(problem);
  }

  async function execute<TData>(path: string, req: RequestOptions): Promise<ApiResult<TData>> {
    const { response, correlationId } = await doFetch(path, req, false);
    await throwIfProblem(response, correlationId);

    if (response.status === 204) {
      return { data: undefined as TData, meta: { correlationId, timestamp: new Date().toISOString(), version: 'v1' } };
    }

    // unwrap the standard envelope { success, data, meta } [STD-API-2]
    const envelope = (await response.json()) as Envelope<TData>;
    if (envelope.success !== true) {
      throw new ApiProblemError(synthesizeProblem(response.status, 'Envelope reported success=false on a 2xx response', correlationId));
    }
    return { data: envelope.data, meta: envelope.meta };
  }

  async function getBlob(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
    const { response, correlationId } = await doFetch(path, { ...opts, method: 'GET' }, false);
    await throwIfProblem(response, correlationId);
    return response.blob();
  }

  async function postBlob(
    path: string,
    body?: unknown,
    opts: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<Blob> {
    // `false` = do not ask for the JSON envelope: the response IS the file.
    const { response, correlationId } = await doFetch(path, { ...opts, method: 'POST', body }, false);
    await throwIfProblem(response, correlationId);
    return response.blob();
  }

  const request = <TData>(path: string, req: RequestOptions = {}) => execute<TData>(path, req);

  return {
    baseUrl: options.baseUrl,
    request,
    get: (path, opts) => request(path, { ...opts, method: 'GET' }),
    post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
    put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
    patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
    delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
    getBlob,
    postBlob
  };
}
